import { Checkbox, FormControl, FormControlLabel, FormHelperText, MenuItem } from '@mui/material';
import { Stack } from '@mui/system';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getUploadStarted, setUseCase } from '../../store/upload/uploadSlice';
import React, { useEffect, useState, useTransition } from 'react';
import MultilineTooltip from '../MultilineTooltip';
import LabelledSelect from '../LabelledSelect';
import { getForceName } from '../../utils/api';

const UseCaseSelector: React.FC<{ forceDisabled: boolean }> = ({ forceDisabled }) => {
	const dispatch = useAppDispatch();

	const useCase = useAppSelector((state) => state.upload.useCase);
	const useCaseConfirmed = useAppSelector((state) => state.upload.useCaseConfirmed);
	const uploadStarted = useAppSelector((state) => getUploadStarted(state));
	const orgs = useAppSelector((state) => state.user.orgs);
	const forceDetails = useAppSelector((state) => state.system.forceDetails);
	const [allowedUseCases, setAllowedUseCases] = useState<Record<string, { rrdDays: number }>>({});
	const changeDisabled = forceDisabled || uploadStarted;
	const [isLoadingForce, startTransition] = useTransition();

	useEffect(() => {
		startTransition(() => {
			const name = getForceName(
				orgs.map((org) => org.name),
				forceDetails
			);
			if (forceDetails[name]) {
				setAllowedUseCases(forceDetails[name].useCases);
			}
		});
	}, []);

	return (
		<MultilineTooltip
			title={
				changeDisabled
					? ['The Use Case chosen cannot be changed after a file upload has started', 'Cancel the file upload to change this value']
					: []
			}
		>
			<FormControl>
				<Stack direction="row" spacing={2} alignContent={'center'}>
					<FormControl sx={{ width: '50%' }} variant="outlined" disabled={changeDisabled || isLoadingForce}>
						<LabelledSelect
							value={useCase}
							onChange={(e) => dispatch(setUseCase({ useCase: e.target.value, confirmed: false }))}
							selectLabel="Use Case"
							testId="use-case-selector"
							required
						>
							{Object.keys(allowedUseCases).map((useCase) => {
								return (
									<MenuItem key={`use-case-selector-${useCase.toLocaleLowerCase()}`} value={useCase.toLocaleLowerCase()}>
										{useCase}
									</MenuItem>
								);
							})}
						</LabelledSelect>
					</FormControl>

					<FormControlLabel
						control={
							<Checkbox
								checked={useCaseConfirmed}
								disabled={!useCase || changeDisabled || isLoadingForce}
								onChange={(_, check) => {
									dispatch(setUseCase({ useCase, confirmed: check }));
								}}
								data-testid="use-case-confirm-checkbox"
							/>
						}
						label={'I confirm that my upload and subsequent processing conforms to the selected use case'}
						required={true}
						sx={{ width: '50%' }}
					/>
				</Stack>

				<FormHelperText>
					A valid use case <i>must</i> be selected and confirmed before any uploads can take place
				</FormHelperText>
			</FormControl>
		</MultilineTooltip>
	);
};

export default UseCaseSelector;
